import { inject } from '@angular/core';
import {
  BaseHierarchicalCrudComponent,
  ICrudService,
  LocationTypes,
} from '@zendra/shared';
import { WoodCertificationDetailService } from 'geographic/proxy';
import {
  GetWoodCertificationDetailProjectionListInputDto,
  WoodCertificationDetailProjectionDto,
  WoodCertificationDetailProjectionExcelDownloadDto,
} from 'projects/orchestrator/src/lib/proxy/upm/zendra/cms/projection/wood-certification-details/models';
import { WoodCertificationDetailPlanningOrchestratorService } from 'projects/orchestrator/src/lib/proxy/orchestrator/controllers/wood-certification-detail-planning-orchestrator.service';
import {
  WoodCertificationDetailOrchestratorPlanningCreateDto,
  WoodCertificationDetailOrchestratorPlanningUpdateDto,
} from 'projects/orchestrator/src/lib/proxy/orchestrator/dtos/wood-certification-detail/models';

export abstract class CertifiedLocationBaseComponent extends BaseHierarchicalCrudComponent<
  WoodCertificationDetailProjectionDto,
  WoodCertificationDetailOrchestratorPlanningCreateDto,
  WoodCertificationDetailOrchestratorPlanningUpdateDto,
  WoodCertificationDetailProjectionExcelDownloadDto
> {
  private readonly geographicService = inject(WoodCertificationDetailService);
  private readonly orchestratorService = inject(
    WoodCertificationDetailPlanningOrchestratorService,
  );

  service: ICrudService<
    WoodCertificationDetailProjectionDto,
    WoodCertificationDetailOrchestratorPlanningCreateDto,
    WoodCertificationDetailOrchestratorPlanningUpdateDto,
    WoodCertificationDetailProjectionExcelDownloadDto
  > = {
    create: input => this.orchestratorService.createCertificationDetailForLocationByInput(input),
    update: (id, input) => this.orchestratorService.updateCertificationDetailForLocationByIdAndInput(id, input),
    projectionConsistency: this.createProjectionConsistency(
      this.orchestratorService,
      'woodCertificationDetailId',
    ),
    deleteByIds: ids => this.geographicService.deleteByIds(ids),
    getList: (query: GetWoodCertificationDetailProjectionListInputDto) =>
      this.orchestratorService.getList(this.withCertificationContext(query)),
    getDownloadToken: () => this.orchestratorService.getDownloadToken(),
    getListAsExcelFile: input => this.orchestratorService.getListAsExcelFile(this.withCertificationContext(input)),
  };

  private withCertificationContext<T extends GetWoodCertificationDetailProjectionListInputDto>(input: T): T {
    let woodCertificationId = input.woodCertificationId;
    if (!woodCertificationId?.length) {
      woodCertificationId = this.parentId !== undefined ? [this.parentId] : undefined;
    }
    return {
      ...input,
      woodCertificationId,
      locationTypeEntityName: this.getWoodCertificationType(),
    };
  }

  protected prepareUpdateDto(
    data: any,
    selected: WoodCertificationDetailProjectionDto,
  ): WoodCertificationDetailOrchestratorPlanningUpdateDto {
    return {
      woodCertificationId: data.woodCertificationId,
      locationId: data.locationId,
      lastCheckDate: selected.lastCheckDate ?? null,
      addedDate: data.addedDate ?? null,
      concurrencyStamp: selected.concurrencyStamp,
    };
  }

  protected abstract getWoodCertificationType(): LocationTypes;
}
